import type { TFunction } from 'i18next';

import type { ModuleVersionState } from '@/common/api/models/moduleVersionState';

export interface VersionSelectorLabelInput {
  version: string;
  workflowState?: ModuleVersionState | string;
  sourceVersion?: string | null;
}

export interface VersionSelectorLabels {
  triggerLabel: string;
  menuLabel: string;
}

const ORIGIN_WORKFLOW_STATES = new Set(['draft', 'pending_approval']);

function hasSourceVersion(sourceVersion?: string | null): sourceVersion is string {
  return typeof sourceVersion === 'string' && sourceVersion.trim().length > 0;
}

function shouldShowSourceOrigin(
  workflowState: ModuleVersionState | string | undefined,
  sourceVersion?: string | null,
): boolean {
  const state = workflowState || 'draft';
  return ORIGIN_WORKFLOW_STATES.has(state) && hasSourceVersion(sourceVersion);
}

/**
 * Builds compact trigger and menu labels for the module editor version selector.
 */
export function getVersionSelectorLabels(
  { version, workflowState, sourceVersion }: VersionSelectorLabelInput,
  t: TFunction,
): VersionSelectorLabels {
  const state = workflowState || 'draft';
  const stateLabel = t(`lecturer.moduleEditor.moduleEditor.versionSelector.states.${state}`);

  if (shouldShowSourceOrigin(workflowState, sourceVersion)) {
    return {
      triggerLabel: t('lecturer.moduleEditor.moduleEditor.versionSelector.triggerWithSource', {
        version,
        sourceVersion,
      }),
      menuLabel: t('lecturer.moduleEditor.moduleEditor.versionSelector.versionNumberWithSource', {
        version,
        state: stateLabel,
        sourceVersion,
      }),
    };
  }

  return {
    triggerLabel: version,
    menuLabel: t('lecturer.moduleEditor.moduleEditor.versionSelector.versionNumber', {
      version,
      state: stateLabel,
    }),
  };
}
